import Image from 'next/legacy/image'
import NextLink from 'next/link'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'

import { breakpoints, colors, fontSizes, fontWeights } from '@/styles'

import burnabyImage from './apartments-for-rent-burnaby.jpg'
import coquitlamImage from './apartments-for-rent-coquitlam.jpg'
import northVancouverImage from './apartments-for-rent-north-van.jpg'
import richmondImage from './apartments-for-rent-richmond.jpg'
import surreyImage from './apartments-for-rent-surrey.jpg'
import vancouverImage from './apartments-for-rent-vancouver.jpg'

const Wrapper = styled.div`
  background: ${colors.white};
  margin: 0 auto;
  width: 100%;
  max-width: ${breakpoints.desktop};
`

const List = styled.div`
  display: grid;
  grid-template-rows: repeat(1, 1fr);
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  max-width: 959px;
  margin: 0 auto;
  margin-top: 40px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const Title = styled.h2`
  color: ${colors.regular};
  font-family: futura-pt, sans-serif;
  font-size: 34px;
  font-weight: ${fontWeights.regular};
  line-height: 42px;
  text-align: center;
  margin: 0;
`

const SubTitle = styled.h3`
  color: ${colors.regular};
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.regular};
  line-height: 28px;
  text-align: center;
  margin: 16px 0 0 0;
`

const Area = styled.div`
  height: 180px;
  width: 100%;
  border-radius: 8px;
  position: relative;
  overflow: hidden;
  z-index: 0;

  &:before {
    content: '';
    width: 100%;
    height: 100%;
    border-radius: 8px;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.5) 100%
    );
    position: absolute;
    z-index: 1;
    left: 0;
    top: 0;
  }
`

const Link = styled.a`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: absolute;
  z-index: 2;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  text-decoration: none;
`

const AreaTitle = styled.h2`
  font-size: 12px;
  line-height: 22px;
  color: ${colors.white};
  font-weight: ${fontWeights.regular};
  margin: 0;
`

const City = styled.h3`
  font-size: 34px;
  line-height: 43px;
  color: ${colors.white};
  font-family: futura-pt, sans-serif;
  font-weight: ${fontWeights.regular};
  margin: 0;
`

const TrendingListings = ({ hideTitle = false }) => {
  const { t } = useTranslation()

  return (
    <Wrapper>
      {!hideTitle && (
        <>
          <Title>
            {t(
              'b.trending_listings.view.trending_listings.title',
              'Trending listings in your neighbourhood'
            )}
          </Title>
          <SubTitle>
            {t(
              'b.trending_listings.view.explore_below.subtitle',
              'See more when you explore the areas below'
            )}
          </SubTitle>
        </>
      )}
      <List>
        <Area>
          <NextLink
            href="/rental-listings/city/vancouver"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.vancouver.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>Vancouver</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={vancouverImage}
            alt="Vancouver BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
        <Area>
          <NextLink
            href="/rental-listings/city/richmond"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.apartments_in_richmond.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>Richmond</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={richmondImage}
            alt="Richmond BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
        <Area>
          <NextLink
            href="/rental-listings/city/surrey"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.apartments_in_surrey.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>Surrey</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={surreyImage}
            alt="Surrey BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
        <Area>
          <NextLink
            href="/rental-listings/city/coquitlam"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.apartments_in_coquitlam.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>Coquitlam</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={coquitlamImage}
            alt="Coquitlam BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
        <Area>
          <NextLink
            href="/rental-listings/city/north-vancouver"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.apartments_in_north_vancouver.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>North Vancouver</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={northVancouverImage}
            alt="North Vancouver BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
        <Area>
          <NextLink
            href="/rental-listings/city/burnaby"
            passHref
            shallow
            legacyBehavior
          >
            <Link>
              <Trans i18nKey="b.trending_listings.view.apartments_in_burnaby.link">
                <AreaTitle>Apartments for rent in</AreaTitle>
                <City>Burnaby</City>
              </Trans>
            </Link>
          </NextLink>
          <Image
            src={burnabyImage}
            alt="Burnaby BC"
            layout="fill"
            objectFit="cover"
            placeholder="blur"
          />
        </Area>
      </List>
    </Wrapper>
  )
}

export default TrendingListings
